import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const API_BASE = 'https://api.glowytics.com';

function getAdminHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-admin-token': localStorage.getItem('adminToken') || '',
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(val, prefix = '') {
  if (val == null || val === '') return '—';
  return `${prefix}${val}`;
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString('tr-TR');
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
      <div style={{
        display: 'inline-block',
        width: 32,
        height: 32,
        border: '3px solid #e5e7eb',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg = type === 'error' ? '#dc2626' : '#059669';
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: bg, color: 'white', borderRadius: 8,
      padding: '12px 20px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxWidth: 360,
    }}>
      {message}
      <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 700 }}>✕</button>
    </div>
  );
}

// ─── Engine Status Badge ──────────────────────────────────────────────────────
function EngineStatusBadge({ status }) {
  const map = {
    active:    { bg: '#d1fae5', color: '#065f46' },
    paused:    { bg: '#f3f4f6', color: '#374151' },
    learning:  { bg: '#fef9c3', color: '#92400e' },
    completed: { bg: '#dbeafe', color: '#1e40af' },
    error:     { bg: '#fee2e2', color: '#991b1b' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: s.bg, color: s.color,
      borderRadius: 6, padding: '3px 12px',
      fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
    }}>
      {status ? status.replace(/_/g, ' ') : '—'}
    </span>
  );
}

// ─── Learning Phase Badge ─────────────────────────────────────────────────────
function LearningPhaseBadge({ status }) {
  const map = {
    LEARNING:        { bg: '#fef9c3', color: '#92400e', label: 'LEARNING' },
    AWAITING_TARGET: { bg: '#ffedd5', color: '#9a3412', label: 'AWAITING TARGET' },
    ACTIVE:          { bg: '#d1fae5', color: '#065f46', label: 'ACTIVE' },
    CEILING_REACHED: { bg: '#dbeafe', color: '#1e40af', label: 'CEILING REACHED' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151', label: status || '—' };
  return (
    <span style={{
      background: s.bg, color: s.color,
      borderRadius: 6, padding: '3px 12px',
      fontSize: 12, fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
}

// ─── Inline editable field ────────────────────────────────────────────────────
function InlineField({ label, value, type = 'text', onSave, readOnly = false, unit = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 200, fontSize: 13, color: '#6b7280', flexShrink: 0 }}>{label}</div>
      {readOnly ? (
        <div style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>
          {value != null ? `${value}${unit}` : '—'}
        </div>
      ) : editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type={type}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            style={{ border: '1px solid #6366f1', borderRadius: 6, padding: '4px 8px', fontSize: 13, width: 120 }}
            autoFocus
          />
          {unit && <span style={{ fontSize: 12, color: '#6b7280' }}>{unit}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            {saving ? '...' : 'Save'}
          </button>
          <button
            onClick={() => { setEditing(false); setDraft(value ?? ''); }}
            style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>
            {value != null && value !== '' ? `${value}${unit}` : '—'}
          </span>
          <button
            onClick={() => setEditing(true)}
            style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 10px', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Multi-select chip field ───────────────────────────────────────────────────
// smartCampaignContract.js ACTIONS ile ayni sozluk. Eski liste ('audience_add',
// 'audience_expand') kontratta karsiligi olmayan v2 kelimeleriydi: kayitli
// allowed_actions hicbir cipe denk gelmiyordu, o yuzden hepsi soluk gorunuyordu.
const ALLOWED_ACTIONS_OPTIONS = [
  'budget_increase',
  'budget_decrease',
  'budget_set',
  'bid_strategy_adjust',
  'campaign_pause',
  'adset_pause',
  'campaign_enable',
  'adset_enable',
  'targeting_refine',
  'audience_update',
];

function AllowedActionsField({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || []); }, [value]);

  const toggle = (action) => {
    setDraft(prev =>
      prev.includes(action) ? prev.filter(a => a !== action) : [...prev, action]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); setEditing(false); }
  };

  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ width: 200, fontSize: 13, color: '#6b7280', flexShrink: 0, paddingTop: 4 }}>Allowed Actions</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ALLOWED_ACTIONS_OPTIONS.map(a => {
              const active = (editing ? draft : (value || [])).includes(a);
              return (
                <span
                  key={a}
                  onClick={() => editing && toggle(a)}
                  style={{
                    background: active ? '#6366f1' : '#f3f4f6',
                    color: active ? 'white' : '#374151',
                    borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 500,
                    cursor: editing ? 'pointer' : 'default',
                    userSelect: 'none',
                    border: active ? '1px solid #6366f1' : '1px solid #e5e7eb',
                  }}
                >
                  {a.replace(/_/g, ' ')}
                </span>
              );
            })}
          </div>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              style={{ marginTop: 6, background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 10px', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}
            >
              Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '4px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
              >
                {saving ? '...' : 'Save'}
              </button>
              <button
                onClick={() => { setEditing(false); setDraft(value || []); }}
                style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ApprovalSlider field ─────────────────────────────────────────────────────
function SliderField({ label, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? 50);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value ?? 50); }, [value]);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); setEditing(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 200, fontSize: 13, color: '#6b7280', flexShrink: 0 }}>{label}</div>
      {editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="range" min={0} max={100} step={1} value={draft} onChange={e => setDraft(Number(e.target.value))} style={{ width: 120 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', minWidth: 32 }}>{draft}%</span>
          <button onClick={handleSave} disabled={saving} style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setDraft(value ?? 50); }} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{value != null ? `${value}%` : '—'}</span>
          <button onClick={() => setEditing(true)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 10px', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}>Edit</button>
        </div>
      )}
    </div>
  );
}

// ─── SelectField ──────────────────────────────────────────────────────────────
function SelectField({ label, value, options, onSave, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); setEditing(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 200, fontSize: 13, color: '#6b7280', flexShrink: 0 }}>{label}</div>
      {readOnly ? (
        <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{value || '—'}</span>
      ) : editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select value={draft} onChange={e => setDraft(e.target.value)} style={{ border: '1px solid #6366f1', borderRadius: 6, padding: '4px 8px', fontSize: 13 }}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={handleSave} disabled={saving} style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setDraft(value ?? ''); }} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{value || '—'}</span>
          <button onClick={() => setEditing(true)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 10px', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}>Edit</button>
        </div>
      )}
    </div>
  );
}

// ─── JSON Viewer ──────────────────────────────────────────────────────────────
function JsonViewer({ data, collapsed = true }) {
  const [open, setOpen] = useState(!collapsed);
  if (!data) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const str = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 10px', fontSize: 11, cursor: 'pointer', color: '#6b7280', marginBottom: 4 }}
      >
        {open ? 'Collapse JSON' : 'Expand JSON'}
      </button>
      {open && (
        <pre style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, fontSize: 11, overflowX: 'auto', maxHeight: 200, margin: 0 }}>
          {str}
        </pre>
      )}
    </div>
  );
}

// ─── Timeline Entry ───────────────────────────────────────────────────────────
function TimelineEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);

  const typeConfig = {
    action:         { color: '#6366f1', bg: '#eff6ff', icon: '⚡', label: 'Action' },
    evaluation:     { color: '#059669', bg: '#f0fdf4', icon: '📊', label: 'Evaluation' },
    recommendation: { color: '#d97706', bg: '#fffbeb', icon: '💡', label: 'Recommendation' },
  };
  const cfg = typeConfig[entry.type] || { color: '#6b7280', bg: '#f9fafb', icon: '•', label: entry.type };

  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
      {/* Left: icon column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: cfg.bg, border: `2px solid ${cfg.color}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0,
        }}>
          {cfg.icon}
        </div>
        <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />
      </div>

      {/* Right: content */}
      <div style={{ flex: 1, paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cfg.label}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(entry.created_at || entry.executed_at || entry.timestamp)}</span>
          {entry.rule_name && <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{entry.rule_name}</span>}
          {entry.execution_status && (
            <span style={{
              background: entry.execution_status === 'executed' ? '#d1fae5' : entry.execution_status === 'failed' ? '#fee2e2' : '#f3f4f6',
              color: entry.execution_status === 'executed' ? '#065f46' : entry.execution_status === 'failed' ? '#991b1b' : '#374151',
              borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600,
            }}>
              {entry.execution_status}
            </span>
          )}
          {entry.status && entry.type === 'recommendation' && (
            <span style={{
              background: entry.status === 'accepted' ? '#d1fae5' : entry.status === 'dismissed' ? '#fee2e2' : '#fef9c3',
              color: entry.status === 'accepted' ? '#065f46' : entry.status === 'dismissed' ? '#991b1b' : '#92400e',
              borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600,
            }}>
              {entry.status}
            </span>
          )}
        </div>

        {/* Summary line */}
        <div style={{ fontSize: 13, color: '#374151' }}>
          {entry.type === 'action' && entry.action_type && (
            <span>
              <strong>{entry.action_type.replace(/_/g, ' ')}</strong>
              {entry.old_value != null && entry.new_value != null && (
                <span style={{ color: '#6b7280' }}> — {entry.old_value} → <strong style={{ color: '#6366f1' }}>{entry.new_value}</strong></span>
              )}
            </span>
          )}
          {entry.type === 'evaluation' && (
            <span>
              Outcome: <strong style={{
                color: entry.outcome === 'IMPROVED' ? '#059669' : entry.outcome === 'WORSENED' ? '#dc2626' : '#374151',
              }}>{entry.outcome || '—'}</strong>
              {entry.improvement_pct != null && (
                <span style={{ color: '#6b7280', marginLeft: 6 }}>({entry.improvement_pct > 0 ? '+' : ''}{entry.improvement_pct}%)</span>
              )}
            </span>
          )}
          {entry.type === 'recommendation' && (
            <span>
              {entry.recommendation_type || entry.type}: {entry.current_value} → {entry.suggested_value}
            </span>
          )}
        </div>

        {entry.reason && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{entry.reason}</div>}
        {entry.reasoning && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{entry.reasoning}</div>}

        {/* Expandable detail */}
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ marginTop: 6, background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 10px', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}
        >
          {expanded ? 'Less' : 'More'}
        </button>

        {expanded && (
          <div style={{ marginTop: 8, padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            {entry.type === 'action' && (
              <div style={{ fontSize: 12 }}>
                <div><strong>Field:</strong> {entry.field || entry.action_type || '—'}</div>
                <div><strong>Old:</strong> {entry.old_value ?? '—'} → <strong>New:</strong> {entry.new_value ?? '—'}</div>
                {entry.metrics_snapshot && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: '#374151' }}>Metrics Snapshot</div>
                    <JsonViewer data={entry.metrics_snapshot} collapsed={false} />
                  </div>
                )}
              </div>
            )}
            {entry.type === 'evaluation' && (
              <div style={{ fontSize: 12 }}>
                <div><strong>Primary Metric Before:</strong> {entry.before_metric ?? '—'}</div>
                <div><strong>Primary Metric After:</strong> {entry.after_metric ?? '—'}</div>
                <div><strong>Improvement:</strong> {entry.improvement_pct != null ? `${entry.improvement_pct}%` : '—'}</div>
              </div>
            )}
            {entry.type === 'recommendation' && (
              <div style={{ fontSize: 12 }}>
                <div><strong>Type:</strong> {entry.recommendation_type || '—'}</div>
                <div><strong>Current:</strong> {entry.current_value ?? '—'}</div>
                <div><strong>Suggested:</strong> {entry.suggested_value ?? '—'}</div>
                <div><strong>Reasoning:</strong> {entry.reasoning || '—'}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ceiling Recommendation Banner ───────────────────────────────────────────
function CeilingBanner({ rec, brandId, onDismiss, onAccept }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [applyValue, setApplyValue] = useState(rec.suggested_value ?? '');
  const [saving, setSaving] = useState(false);

  const handleAccept = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/limit-recommendations/${rec.id}`, {
        method: 'PATCH',
        headers: getAdminHeaders(),
        body: JSON.stringify({ status: 'accepted', apply_value: applyValue }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Accept failed');
      onAccept();
    } catch (e) {
      alert('Error accepting: ' + e.message);
    } finally {
      setSaving(false);
      setModalOpen(false);
    }
  };

  const handleDismiss = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/limit-recommendations/${rec.id}`, {
        method: 'PATCH',
        headers: getAdminHeaders(),
        body: JSON.stringify({ status: 'dismissed' }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Dismiss failed');
      onDismiss();
    } catch (e) {
      alert('Error dismissing: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        padding: '14px 20px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ fontSize: 13, color: '#1e40af' }}>
          <strong>Engine Budget Ceiling Suggestion:</strong> {rec.current_value} → <strong>{rec.suggested_value}</strong>
          {rec.reasoning && <span style={{ color: '#3730a3', marginLeft: 6 }}>({rec.reasoning})</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setModalOpen(true)}
            style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            Accept
          </button>
          <button
            onClick={handleDismiss}
            disabled={saving}
            style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}
          >
            Dismiss
          </button>
        </div>
      </div>

      {modalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 400, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#111827' }}>Accept Ceiling Recommendation</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>Adjust the apply value below if needed:</div>
            <input
              type="number"
              value={applyValue}
              onChange={e => setApplyValue(e.target.value)}
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 12px', fontSize: 14, marginBottom: 20, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalOpen(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              <button onClick={handleAccept} disabled={saving} style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Confirm Accept'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Metrics Chart Panel ──────────────────────────────────────────────────────
function MetricsChart({ timelineData }) {
  const [visibleLines, setVisibleLines] = useState({ roas: true, cpa: true, ctr: true, spend: true });

  // Extract metrics from action entries with metrics_snapshot
  const chartData = (timelineData || [])
    .filter(e => e.type === 'action' && e.metrics_snapshot)
    .slice(-14) // last 14 entries
    .map(e => {
      const snap = typeof e.metrics_snapshot === 'string'
        ? JSON.parse(e.metrics_snapshot)
        : e.metrics_snapshot;
      const ts = e.created_at || e.executed_at || e.timestamp;
      return {
        date: ts ? new Date(ts).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }) : '?',
        roas: snap?.roas ?? snap?.value_per_result ?? null,
        cpa: snap?.cpa ?? snap?.cost_per_result ?? null,
        ctr: snap?.ctr ?? null,
        spend: snap?.spend ?? snap?.amount_spent ?? null,
      };
    })
    .filter(d => d.roas != null || d.cpa != null || d.ctr != null || d.spend != null);

  const toggleLine = (key) => {
    setVisibleLines(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (chartData.length === 0) {
    return (
      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 24 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 8 }}>Metrics Chart</div>
        <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 32 }}>No metrics snapshots in timeline data.</div>
      </div>
    );
  }

  const lines = [
    { key: 'roas', color: '#6366f1', label: 'ROAS' },
    { key: 'cpa', color: '#ef4444', label: 'CPA' },
    { key: 'ctr', color: '#f59e0b', label: 'CTR' },
    { key: 'spend', color: '#10b981', label: 'Spend' },
  ];

  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>Metrics Timeline (from action snapshots)</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {lines.map(l => (
            <button
              key={l.key}
              onClick={() => toggleLine(l.key)}
              style={{
                background: visibleLines[l.key] ? l.color : '#f3f4f6',
                color: visibleLines[l.key] ? 'white' : '#9ca3af',
                border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12,
                cursor: 'pointer', fontWeight: 500,
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Legend />
          {lines.map(l => visibleLines[l.key] && (
            <Line
              key={l.key}
              type="monotone"
              dataKey={l.key}
              stroke={l.color}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name={l.label}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SmartCampaignDetail() {
  const { brandId, configId } = useParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [brandInfo, setBrandInfo] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [ceilingRecs, setCeilingRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [timelineDays, setTimelineDays] = useState(30);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Learning phase form state
  const [learningForm, setLearningForm] = useState({ target_roas: '', roas_breakeven: '', success_metric: 'ROAS' });
  const [learningSubmitting, setLearningSubmitting] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  // ── Fetch config (via brand dashboard, pick by configId) ──
  const fetchConfig = useCallback(() => {
    setLoading(true);
    setError(null);
    // /dashboard yalnizca birkac kolon seciyor (campaign_objective, rule_ids,
    // latest_metrics, target_roas, engine_status hepsi disarida) — config'i
    // oradan cekmek sayfayi yariya yakin "—" gosteriyordu. Tam satir icin
    // /campaigns/:configId, marka adi icin /dashboard.
    Promise.all([
      fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/campaigns/${configId}`, {
        headers: getAdminHeaders(), credentials: 'include',
      }).then(r => r.json()),
      fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/dashboard`, {
        headers: getAdminHeaders(), credentials: 'include',
      }).then(r => r.json()).catch(() => null),
    ])
      .then(([cfgRes, dashRes]) => {
        const d = dashRes?.data || dashRes;
        setBrandInfo({ name: d?.brandName || d?.name, id: brandId });
        const found = cfgRes?.data?.config || null;
        setConfig(found);
        if (!found) setError('Campaign config not found.');
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [brandId, configId]);

  // ── Fetch timeline ──
  const fetchTimeline = useCallback(() => {
    setTimelineLoading(true);
    fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/configs/${configId}/timeline?days=${timelineDays}`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        setTimeline(data.data || data.timeline || data || []);
        setTimelineLoading(false);
      })
      .catch(() => setTimelineLoading(false));
  }, [brandId, configId, timelineDays]);

  // ── Fetch ceiling recommendations ──
  const fetchCeilingRecs = useCallback(() => {
    fetch(`${API_BASE}/api/admin/smart/limit-recommendations?brandId=${brandId}&status=pending`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        const all = data.data || data || [];
        // Filter to this campaign config
        const filtered = all.filter(r => r.campaign_config_id === configId || !r.campaign_config_id);
        setCeilingRecs(filtered);
      })
      .catch(() => {});
  }, [brandId, configId]);

  useEffect(() => {
    fetchConfig();
    fetchTimeline();
    fetchCeilingRecs();
  }, [fetchConfig, fetchTimeline, fetchCeilingRecs]);

  useEffect(() => {
    fetchTimeline();
  }, [timelineDays, fetchTimeline]);

  // ── PATCH limits ──
  const patchLimit = useCallback(async (field, value) => {
    const res = await fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/configs/${configId}/limits`, {
      method: 'PATCH',
      headers: getAdminHeaders(),
      credentials: 'include',
      body: JSON.stringify({ [field]: value }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Save failed');
    }
    showToast(`${field.replace(/_/g, ' ')} updated.`);
    fetchConfig();
  }, [brandId, configId, showToast, fetchConfig]);

  // ── Learning phase submit ──
  const handleLearningSubmit = async () => {
    if (!learningForm.target_roas || !learningForm.roas_breakeven) {
      showToast('target_roas and roas_breakeven are required.', 'error');
      return;
    }
    setLearningSubmitting(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/smart/brands/${brandId}/configs/${configId}/learning-phase/complete`,
        {
          method: 'POST',
          headers: getAdminHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            target_roas: parseFloat(learningForm.target_roas),
            roas_breakeven: parseFloat(learningForm.roas_breakeven),
            success_metric: learningForm.success_metric,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to activate engine');
      }
      showToast('Engine activated successfully!');
      fetchConfig();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLearningSubmitting(false);
    }
  };

  // ── Loading skeleton ──
  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <Spinner />
      </div>
    );
  }

  // ── Error state ──
  if (error && !config) {
    return (
      <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <button
          onClick={() => navigate('/hq/admin/smart-campaigns')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 14, marginBottom: 20, fontWeight: 500, padding: 0 }}
        >
          ← Back to Smart Campaigns
        </button>
        <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '14px 20px', fontSize: 13 }}>
          {error}
        </div>
      </div>
    );
  }

  const learningPhaseStatus = config?.learning_phase_status;
  const engineStatus = config?.engine_status || 'paused';
  const metaLink = /^meta/i.test(config?.platform || '') && config?.campaign_id
    ? `https://business.facebook.com/adsmanager/manage/campaigns?act=${config?.account_id || ''}&campaign_ids=${config?.campaign_id}`
    : null;

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate('/hq/admin/smart-campaigns')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, marginBottom: 12, fontWeight: 500, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          ← Back to Smart Campaigns
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {brandInfo?.name || brandId}
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0, marginBottom: 8 }}>
              {config?.campaign_name || config?.campaign_id || 'Campaign Detail'}
            </h1>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <EngineStatusBadge status={engineStatus} />
              {learningPhaseStatus && <LearningPhaseBadge status={learningPhaseStatus} />}
              {config?.platform && (
                <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>
                  {config.platform.toUpperCase()}
                </span>
              )}
              {metaLink && (
                <a
                  href={metaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: '#1877f2', color: 'white', borderRadius: 6, padding: '3px 12px', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                >
                  Open in Meta
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Error banner (non-fatal) ── */}
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* ── Learning Phase Box ── */}
      {learningPhaseStatus === 'AWAITING_TARGET' && (
        <div style={{
          background: '#fff7ed', border: '2px solid #f97316', borderRadius: 12,
          padding: '20px 24px', marginBottom: 24,
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#9a3412', marginBottom: 8 }}>
            Learning tamamlandi. ROAS Target girerek engine'i aktif edin.
          </div>
          <div style={{ fontSize: 13, color: '#c2410c', marginBottom: 16 }}>
            Engine learning phase'i tamamladi. Hedef ROAS ve breakeven degerlerini girin.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#9a3412', display: 'block', marginBottom: 4 }}>Hedef ROAS *</label>
              <input
                type="number"
                step="0.1"
                placeholder="4.0"
                value={learningForm.target_roas}
                onChange={e => setLearningForm(f => ({ ...f, target_roas: e.target.value }))}
                style={{ border: '1px solid #f97316', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: 110 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#9a3412', display: 'block', marginBottom: 4 }}>Kirilim Noktasi *</label>
              <input
                type="number"
                step="0.1"
                placeholder="2.0"
                value={learningForm.roas_breakeven}
                onChange={e => setLearningForm(f => ({ ...f, roas_breakeven: e.target.value }))}
                style={{ border: '1px solid #f97316', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: 110 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#9a3412', display: 'block', marginBottom: 4 }}>Success Metric</label>
              <select
                value={learningForm.success_metric}
                onChange={e => setLearningForm(f => ({ ...f, success_metric: e.target.value }))}
                style={{ border: '1px solid #f97316', borderRadius: 6, padding: '7px 10px', fontSize: 13 }}
              >
                {['ROAS', 'CPA', 'CTR', 'COMPOSITE'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <button
              onClick={handleLearningSubmit}
              disabled={learningSubmitting}
              style={{
                background: '#f97316', color: 'white', border: 'none', borderRadius: 8,
                padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {learningSubmitting ? 'Aktivasyon...' : "Engine'i Aktif Et"}
            </button>
          </div>
        </div>
      )}

      {/* ── Ceiling Recommendation Banners ── */}
      {ceilingRecs.map(rec => (
        <CeilingBanner
          key={rec.id}
          rec={rec}
          brandId={brandId}
          onDismiss={() => { setCeilingRecs(prev => prev.filter(r => r.id !== rec.id)); showToast('Recommendation dismissed.'); }}
          onAccept={() => { setCeilingRecs(prev => prev.filter(r => r.id !== rec.id)); showToast('Recommendation accepted!'); fetchConfig(); }}
        />
      ))}

      {/* ── Metrics Chart ── */}
      <MetricsChart timelineData={timeline} />

      {/* ── Two-column layout: Timeline + Right sidebar ── */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── Decision Timeline ── */}
        <div style={{ flex: 2, minWidth: 320 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>Decision Timeline</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[30, 60, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setTimelineDays(d)}
                    style={{
                      background: timelineDays === d ? '#6366f1' : '#f3f4f6',
                      color: timelineDays === d ? 'white' : '#374151',
                      border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12,
                      cursor: 'pointer', fontWeight: timelineDays === d ? 600 : 400,
                    }}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {timelineLoading ? (
              <Spinner />
            ) : timeline.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 32 }}>
                No timeline events found for the selected period.
              </div>
            ) : (
              <div>
                {timeline.map((entry, i) => (
                  <TimelineEntry key={entry.id || i} entry={entry} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Right sidebar: Limit Editor + Current State ── */}
        <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Limit Editor */}
          <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 16 }}>Limit Editor</div>

            <InlineField
              label="Max Daily Budget"
              value={config?.budget_max_daily}
              type="number"
              unit=""
              onSave={v => patchLimit('budget_max_daily', parseFloat(v))}
            />
            <InlineField
              label="Min Daily Budget"
              value={config?.budget_min_daily}
              type="number"
              onSave={v => patchLimit('budget_min_daily', parseFloat(v))}
            />
            <InlineField
              label="Lifetime Budget Max"
              value={config?.budget_lifetime_max}
              type="number"
              onSave={v => patchLimit('budget_lifetime_max', parseFloat(v))}
            />
            <SliderField
              label="Approval Threshold (%)"
              value={config?.approval_threshold_pct}
              onSave={v => patchLimit('approval_threshold_pct', v)}
            />
            <AllowedActionsField
              value={config?.allowed_actions}
              onSave={v => patchLimit('allowed_actions', v)}
            />
            <SelectField
              label="Success Metric"
              value={config?.success_metric}
              options={['ROAS', 'CPA', 'CTR', 'COMPOSITE']}
              onSave={v => patchLimit('success_metric', v)}
            />
            <InlineField
              label="Target ROAS"
              value={config?.target_roas}
              type="number"
              readOnly={learningPhaseStatus !== 'ACTIVE'}
              onSave={v => patchLimit('target_roas', parseFloat(v))}
            />
            <InlineField
              label="Target CPA"
              value={config?.target_cpa}
              type="number"
              onSave={v => patchLimit('target_cpa', parseFloat(v))}
            />
          </div>

          {/* Current State Panel */}
          <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 16 }}>Current State</div>

            {[
              { label: 'Platform', value: config?.platform },
              { label: 'Ad Account ID', value: config?.account_id },
              { label: 'Campaign Objective', value: config?.campaign_objective },
              { label: 'Campaign Type', value: config?.campaign_type },
              { label: 'Platform Campaign ID', value: config?.campaign_id },
              { label: 'Last Engine Check', value: fmtDate(config?.last_checked_at) },
              { label: 'Last Action At', value: fmtDate(config?.last_action_at) },
              { label: 'Learning Completed At', value: fmtDate(config?.learning_completed_at) },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ width: 160, fontSize: 12, color: '#6b7280', flexShrink: 0 }}>{label}</div>
                <div style={{ fontSize: 12, color: '#111827', fontWeight: 500, wordBreak: 'break-all' }}>{value || '—'}</div>
              </div>
            ))}

            {/* Rule IDs */}
            <div style={{ padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Assigned Rule IDs</div>
              {config?.rule_ids && config.rule_ids.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {config.rule_ids.map(rid => (
                    <span key={rid} style={{ background: '#eff6ff', color: '#1e40af', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontFamily: 'monospace' }}>
                      {rid}
                    </span>
                  ))}
                </div>
              ) : <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>}
            </div>

            {/* Latest metrics snapshot */}
            <div style={{ padding: '7px 0' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Latest Metrics Snapshot</div>
              <JsonViewer data={config?.latest_metrics || config?.metrics_snapshot} collapsed />
            </div>
          </div>

        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
